/**
 * Validates a `schemaType: "sql"` field's default value against the actual
 * Postgres column constraints its type provisions (see
 * `IngestTableService.fieldTypeToDdl`). Shared by the backend
 * (`SchemaDefinitionService`, authoritative) and the frontend
 * (`sqlSchemaParser`, row editors) so both reject the same values.
 */

import Decimal from "decimal.js";
import type { SqlSchemaFieldType } from "@erp/shared-types";
import { parseFlexibleDateOnly } from "./date";

/** Postgres VARCHAR(n) length backing the "sql" schema "string" type — see `IngestTableService.fieldTypeToDdl`. */
export const SQL_SCHEMA_STRING_MAX_LENGTH = 255;
/** Postgres INTEGER (int4) bounds backing the "sql" schema "integer" type — see `IngestTableService.fieldTypeToDdl`. */
export const SQL_SCHEMA_INTEGER_MIN = -2_147_483_648;
export const SQL_SCHEMA_INTEGER_MAX = 2_147_483_647;

export type SqlNumericArgs = {
  precision?: number;
  scale?: number;
};

/**
 * Whether `value` fits within a NUMERIC(precision, scale) column exactly —
 * no more than `scale` digits after the decimal point, and no more digits
 * before it than `precision - scale` allows. Never rounds: a value that
 * doesn't fit is invalid, not silently truncated.
 */
export function fitsNumericPrecision(
  value: number,
  precision: number,
  scale: number,
): boolean {
  if (!Number.isFinite(value)) return false;
  const decimal = new Decimal(value);
  if (decimal.decimalPlaces() > scale) return false;
  const integerPart = decimal.abs().trunc();
  const integerDigits = integerPart.isZero()
    ? 1
    : integerPart.toFixed(0).length;
  return integerDigits <= precision - scale;
}

/**
 * Checks a "sql" schema field's default value against its type's Postgres
 * column constraints. Returns a short description of the violation (fit for
 * appending to a larger "Field X has an invalid default: ..." message), or
 * `null` if the value is valid. `null`/`undefined` values are always valid
 * (no default).
 */
export function sqlDefaultValueConstraintError(
  fieldType: SqlSchemaFieldType,
  value: unknown,
  numericArgs?: SqlNumericArgs | null,
): string | null {
  if (value == null) return null;

  switch (fieldType) {
    case "string":
      return stringConstraintError(value, true);
    case "text":
      return stringConstraintError(value, false);
    case "float":
      return finiteNumberConstraintError(value);
    case "boolean":
      return booleanConstraintError(value);
    case "integer":
      return integerConstraintError(value);
    case "numeric":
      return numericConstraintError(value, numericArgs);
    case "date":
      return dateConstraintError(value);
  }
}

function stringConstraintError(
  value: unknown,
  limited: boolean,
): string | null {
  if (typeof value !== "string") return "must be a string";
  if (limited && value.length > SQL_SCHEMA_STRING_MAX_LENGTH) {
    return `must be ${SQL_SCHEMA_STRING_MAX_LENGTH} characters or fewer (got ${value.length})`;
  }
  return null;
}

function finiteNumberConstraintError(value: unknown): string | null {
  return typeof value === "number" && Number.isFinite(value)
    ? null
    : "must be a number";
}

function booleanConstraintError(value: unknown): string | null {
  return typeof value === "boolean" ? null : "must be a boolean";
}

function integerConstraintError(value: unknown): string | null {
  if (typeof value !== "number" || !Number.isInteger(value)) {
    return "must be a whole number";
  }
  if (value < SQL_SCHEMA_INTEGER_MIN || value > SQL_SCHEMA_INTEGER_MAX) {
    return `must be between ${SQL_SCHEMA_INTEGER_MIN} and ${SQL_SCHEMA_INTEGER_MAX}`;
  }
  return null;
}

function numericConstraintError(
  value: unknown,
  numericArgs?: SqlNumericArgs | null,
): string | null {
  const numberError = finiteNumberConstraintError(value);
  if (numberError) return numberError;

  const precision = numericArgs?.precision;
  const scale = numericArgs?.scale;
  if (typeof precision !== "number" || typeof scale !== "number") {
    // The field-level precision/scale requirement is enforced elsewhere
    // (SchemaDefinitionService / sqlSchemaParser); nothing more to check here.
    return null;
  }
  if (
    typeof value !== "number" ||
    fitsNumericPrecision(value, precision, scale)
  ) {
    return null;
  }
  const integerDigits = precision - scale;
  const digitLabel = integerDigits === 1 ? "digit" : "digits";
  return `must fit NUMERIC(${precision}, ${scale}) — at most ${integerDigits} ${digitLabel} before the decimal point and ${scale} after`;
}

function dateConstraintError(value: unknown): string | null {
  if (typeof value !== "string" || parseFlexibleDateOnly(value) === null) {
    return "must be a valid date (YYYY-MM-DD, YYYYMMDD, or YYYY/MM/DD)";
  }
  return null;
}
